import { Pipe, PipeTransform, OnDestroy, ChangeDetectorRef, NgZone, inject, ApplicationRef } from '@angular/core';
import { AutoTranslateService } from 'app/services/auto-translate.service';
import { LanguageService, SupportedLanguage } from 'app/services/language.service';
import { Subscription, firstValueFrom } from 'rxjs';

@Pipe({
    name: 'autoTranslate',
    standalone: true,
    pure: false,
})
export class AutoTranslatePipe implements PipeTransform, OnDestroy {
    private translateService = inject(AutoTranslateService);
    private languageService = inject(LanguageService);
    private cdr = inject(ChangeDetectorRef);
    private ngZone = inject(NgZone);
    private appRef = inject(ApplicationRef);

    private cache: Map<string, string> = new Map();
    private lastLanguage: SupportedLanguage = 'pt-br';
    private languageSubscription?: Subscription;
    private pendingTranslations: Map<string, Promise<string>> = new Map();
    private static refreshScheduled = false;

    constructor() {
        this.lastLanguage = this.languageService.getCurrentLanguage();
        this.languageSubscription = this.languageService.getLanguage$().subscribe((language) => {
            if (language === this.lastLanguage) {
                return;
            }
            this.lastLanguage = language;
            this.cache.clear();
            this.pendingTranslations.clear();
            this.scheduleViewRefresh();
        });
    }

    transform(value: string | null | undefined): string {
        if (!value || typeof value !== 'string' || value.trim() === '') {
            return value || '';
        }

        const trimmedValue = value.trim();
        const currentLanguage = this.languageService.getCurrentLanguage();

        if (currentLanguage === 'pt-br') {
            return trimmedValue;
        }

        const cacheKey = `${trimmedValue}_${currentLanguage}`;

        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey)!;
        }

        if (this.pendingTranslations.has(cacheKey)) {
            return trimmedValue;
        }

        const translationPromise = firstValueFrom(this.translateService.translate(trimmedValue, currentLanguage))
            .then((translated) => {
                this.cache.set(cacheKey, translated);
                this.pendingTranslations.delete(cacheKey);
                this.scheduleViewRefresh();
                return translated;
            })
            .catch(() => {
                this.cache.set(cacheKey, trimmedValue);
                this.pendingTranslations.delete(cacheKey);
                this.scheduleViewRefresh();
                return trimmedValue;
            });

        this.pendingTranslations.set(cacheKey, translationPromise);

        return trimmedValue;
    }

    ngOnDestroy(): void {
        if (this.languageSubscription) {
            this.languageSubscription.unsubscribe();
        }
        this.cache.clear();
        this.pendingTranslations.clear();
    }

    /** Garante re-render após troca de idioma / fim da tradução (Default CD + iframe). */
    private scheduleViewRefresh(): void {
        if (AutoTranslatePipe.refreshScheduled) {
            return;
        }
        AutoTranslatePipe.refreshScheduled = true;
        this.ngZone.runOutsideAngular(() => {
            queueMicrotask(() => {
                this.ngZone.run(() => {
                    AutoTranslatePipe.refreshScheduled = false;
                    try {
                        this.cdr.markForCheck();
                        this.cdr.detectChanges();
                    } catch {
                        /* view destruída */
                    }
                    try {
                        this.appRef.tick();
                    } catch {
                        /* app estável */
                    }
                });
            });
        });
    }
}
